import { useQueries } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { practitionerByIdQueryKey } from '@/settings/services/use-practitioner';
import type { Practitioner } from '@/types/settings-api';
import { retryTransientOnce } from './use-patient-lab-results';

/**
 * Names for the user ids a lab report carries (`confirmedBy`, `createdBy`), PROD-3275.
 *
 * The results API sends only ids. The one existing user lookup is the practitioner read
 * (`GET /api/v1/Practice/Practitioner/{userId}`, `usePractitionerById`); this reads it for several
 * ids at once, on the same cache key, so a name already loaded elsewhere is reused. A user who is
 * not a practitioner (a 404) or a read that fails has no name here: the caller then shows the line
 * without one, never the raw id.
 */
export function useLabUserNames(userIds: Array<string | null | undefined>): Map<string, string> {
  const ids = [...new Set(userIds.filter((id): id is string => !!id))];
  const results = useQueries({
    queries: ids.map((userId) => ({
      queryKey: practitionerByIdQueryKey(userId),
      queryFn: ({ signal }: { signal?: AbortSignal }) =>
        get<Practitioner>(`/api/v1/Practice/Practitioner/${encodeURIComponent(userId)}`, { signal }),
      staleTime: 5 * 60_000,
      retry: retryTransientOnce,
    })),
  });
  const names = new Map<string, string>();
  ids.forEach((id, i) => {
    const n = results[i]?.data?.name;
    const full = [n?.firstName, n?.lastName].map((x) => x?.trim()).filter(Boolean).join(' ');
    if (full) names.set(id, full);
  });
  return names;
}
